import React, { useState, useCallback } from 'react';
import { Modal, Typography, useToast, Input, Form } from '@crisellevaldez/speculo-ui';
import { Info } from 'lucide-react';
import { LoadingDialog } from '../../common/dialogs/LoadingDialog';
import { ConvertFlow } from '../../../hooks/useFlowsQuery';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { useQueryClient } from '@tanstack/react-query';
import {
  buildTransferLineOptions,
  formatTransferNumbers,
  stripPhone,
} from '../../../utils/flows/formatTransferNumbers';
import { useUpdateFlow } from '../../../hooks/flows/useUpdateFlow';
import { useOrganizationUsers } from '../../../hooks/useOrganizationUsers';
import { type TeamMemberInput } from '../../../schemas/teamMember.schema';
import { AddTeamMemberModal } from './AddTeamMemberModal';
import { FlowTransferLines, FlowVoicemail, FlowActions } from './edit';
import { FormattedTextarea } from '../../common/FormattedTextarea';
import { Tooltip } from '../../common/Tooltip';
import { ImproveWithAIButton } from './ImproveWithAIButton';
import { useUser } from '../../../hooks/useUser';
import { AdminLockOverlay } from '../client/AdminLockOverlay';

interface EditConvertFlowModalProps {
  isOpen: boolean;
  onClose: () => void;
  flow: ConvertFlow | null;
}

export const EditConvertFlowModal: React.FC<EditConvertFlowModalProps> = ({
  isOpen,
  onClose,
  flow,
}) => {
  const { organization } = useOrganizationStore();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { users, addTeamMember } = useOrganizationUsers();
  const { updateFlow: updateFlowMutation, isUpdating } = useUpdateFlow();

  // State
  const { isAdmin } = useUser();
  const [isAddingTeamMember, setIsAddingTeamMember] = useState(false);
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [source, setSource] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [descriptionError, setDescriptionError] = useState<string>('');
  const [selectedDescription, setSelectedDescription] = useState<string>('');
  const [transferLines, setTransferLines] = useState<string[]>([]);
  const [voicemailContent, setVoicemailContent] = useState('');
  const [showAddTeamMember, setShowAddTeamMember] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [useRoundRobin, setUseRoundRobin] = useState(false);

  // Validation functions
  const validateName = useCallback((value: string) => {
    if (!value.trim()) {
      setNameError('Flow name is required');
      return false;
    }
    setNameError('');
    return true;
  }, []);

  const validateDescription = useCallback((value: string) => {
    if (!value.trim()) {
      setDescriptionError('Description is required');
      return false;
    }
    setDescriptionError('');
    return true;
  }, []);

  // Form validation
  const validateForm = (): boolean => {
    let isValid = true;
    isValid = validateName(name) && isValid;
    isValid = validateDescription(description) && isValid;
    return isValid;
  };

  // Update handler
  const handleUpdate = async () => {
    if (!flow?.id) {
      showToast({
        message: 'Flow ID is missing',
        variant: 'error',
      });
      return;
    }

    if (!validateForm()) return;

    try {
      await updateFlowMutation({
        id: flow.id,
        name,
        lead_source: source,
        description,
        description_id: selectedDescription,
        flow_type: 'Convert',
        voicemail: voicemailContent,
        useRoundRobin: useRoundRobin,
        // SPEC-1596 fix: previously this filtered `users.value` (userId
        // UUID) against `transferLines` (phone numbers), which never matched
        // — so every save through this modal silently wiped
        // transfer_numbers. Mirror EditFlowModal: build {value: phone,
        // label: "Name (phone)"} options first, then filter by phone.
        transfer_numbers: formatTransferNumbers(
          buildTransferLineOptions(users, flow?.transfer_numbers).filter((u) =>
            transferLines.some((t) => stripPhone(t) === stripPhone(u.value))
          )
        ),
      });

      showToast({
        message: 'Flow updated successfully',
        variant: 'success',
      });
    } catch (error) {
      showToast({
        message: error instanceof Error ? error.message : 'Failed to update flow',
        variant: 'error',
      });
    }
  };

  // Reset form when modal closes
  React.useEffect(() => {
    const resetForm = () => {
      setName('');
      setNameError('');
      setSource('');
      setDescription('');
      setDescriptionError('');
      setSelectedDescription('');
      setTransferLines([]);
      setVoicemailContent('');
      setIsTesting(false);
      setIsAddingTeamMember(false);
      setShowAddTeamMember(false);
      setUseRoundRobin(false);
    };

    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  // Initialize form values when flow changes or modal opens
  React.useEffect(() => {
    if (!flow || !isOpen) return;

    // Basic info
    setName(flow.name || '');
    validateName(flow.name || '');
    setSource(flow.lead_source || '');

    // Set description and description_id
    setDescription(flow.description || '');
    setSelectedDescription(flow.description_id || '');
    validateDescription(flow.description || '');

    // Transfer lines
    if (flow.transfer_numbers) {
      const phoneNumbers = Object.values(flow.transfer_numbers);
      setTransferLines(phoneNumbers);
    } else {
      setTransferLines([]);
    }

    // Set useRoundRobin
    setUseRoundRobin(flow.useRoundRobin === true);
    // Initialize voicemail from GQL data (Firestore fallback handled in FlowVoicemail)
    setVoicemailContent(flow.voicemailContent || '');
  }, [flow, validateName, validateDescription, isOpen]);

  React.useEffect(() => {
    if (!flow?.transfer_numbers || !isOpen || users.length === 0) return;
    const options = buildTransferLineOptions(users, flow.transfer_numbers);
    setTransferLines((prev) =>
      prev.map((stored) => {
        const match = options.find((o) => stripPhone(o.value) === stripPhone(stored));
        return match ? match.value : stored;
      })
    );
  }, [users, flow?.transfer_numbers, isOpen]);

  if (!flow) return null;

  return (
    <>
      <LoadingDialog
        isOpen={isUpdating || isTesting}
        message={isTesting ? 'Testing call...' : 'Updating flow...'}
      />
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <Modal.Header>
          <Typography variant="h3">Edit Convert Flow</Typography>
        </Modal.Header>
        <Modal.Body>
          <AdminLockOverlay locked={!isAdmin} subject="Flows">
          <div className="space-y-6">
            {/* Name and Source */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Form.Control>
                <Form.Label>Name</Form.Label>
                <Input
                  placeholder="Enter flow name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    validateName(e.target.value);
                  }}
                  error={nameError}
                />
              </Form.Control>
              {(() => {
                const isProtectedSource =
                  source === 'speculo_seller' || source === 'speculo_buyer';
                return (
                  <Form.Control>
                    <Form.Label>Source</Form.Label>
                    <Input
                      placeholder="Enter source"
                      value={source}
                      onChange={(e) => setSource(e.target.value)}
                      disabled={isProtectedSource}
                      title={
                        isProtectedSource
                          ? `Source "${source}" is managed by Speculo and cannot be changed`
                          : undefined
                      }
                    />
                  </Form.Control>
                );
              })()}
            </div>

            {/* Lead Email (Read-only) */}
            <Form.Control>
              <Form.Label>Lead Email</Form.Label>
              <Input value={flow.lead_email} disabled />
            </Form.Control>

            {/* Description */}
            <Form.Control>
              <div className="flex items-center gap-3 mb-2">
                <Form.Label className="mb-0">Description</Form.Label>
                <Tooltip
                  content="Edit the description directly or click 'Improve Description' to enhance your description and make it more professional and clear."
                  position="right"
                >
                  <span className="group rounded-full transition-colors cursor-help flex items-center">
                    <Info className="w-4 h-4 text-gray-400 group-hover:text-yellow-600" />
                  </span>
                </Tooltip>
                <ImproveWithAIButton
                  text={description}
                  onImproved={setDescription}
                />
              </div>
              <FormattedTextarea
                value={description}
                onChange={(value: string) => {
                  setDescription(value);
                  validateDescription(value);
                }}
                placeholder="We are calling people who just requested more information on a property on our website"
                error={descriptionError}
              />
            </Form.Control>

            {/* Voicemail */}
            <FlowVoicemail
              flow={flow}
              voicemailContent={voicemailContent}
              onVoicemailContentChange={setVoicemailContent}
              disabled={!isAdmin}
            />

            {/* Transfer Lines */}
            <FlowTransferLines
              flow={flow}
              transferLines={transferLines}
              onTransferLinesChange={setTransferLines}
              onAddTeamMemberClick={() => setShowAddTeamMember(true)}
              disabled={!isAdmin}
              useRoundRobin={useRoundRobin}
              onUseRoundRobinChange={setUseRoundRobin}
            />
          </div>
          </AdminLockOverlay>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex flex-col w-full gap-2">
            <div className="flex justify-between items-center">
              <FlowActions
                flow={flow}
                organizationId={organization?.id || ''}
                currentDescription={description}
                canEdit={isAdmin}
                visibleButtons={{
                  update: true,
                  schedule: false,
                  delete: true,
                  testCall: true,
                  pause: false,
                  resume: false,
                }}
                onClose={onClose}
                onUpdate={handleUpdate}
                isUpdating={isUpdating}
                isScheduling={false}
                disabled={{
                  update: !name.trim() || !description.trim(),
                  schedule: false,
                }}
              />
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <AddTeamMemberModal
        isOpen={showAddTeamMember}
        onClose={() => {
          setIsAddingTeamMember(false);
          setShowAddTeamMember(false);
        }}
        onSubmit={async (data: TeamMemberInput) => {
          setIsAddingTeamMember(true);
          try {
            if (!organization?.id) {
              throw new Error('No organization ID');
            }
            await addTeamMember(organization.id, data);
            await queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
            setShowAddTeamMember(false);
            showToast({ message: 'Team member invited successfully', variant: 'success' });
          } catch (error) {
            showToast({
              message: error instanceof Error ? error.message : 'Error adding team member',
              variant: 'error',
            });
          } finally {
            setIsAddingTeamMember(false);
          }
        }}
        isSubmitting={isAddingTeamMember}
      />
    </>
  );
};
